<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          Programs
        </template>
        <template #actions v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')">
          <ul>
            <li class="p-narrow no-border">
              <div class="list-content">
                <a role="button"
                  @keypress.enter.prevent="editPanel()"
                  @click="editPanel()"
                  :class="{ disabled: editOtherPanels || loading || loadingError }"
                  v-if="!editCurrentPanel"
                  :tabindex="editOtherPanels || loading || loadingError ? -1 : 0"
                >
                  <font-awesome-icon :icon="['far', 'edit']" size="lg" title="Edit" />
                </a>
              </div>
            </li>
          </ul>
        </template>
      </EsHeader>
    </div>
    <div class="content">
      <es-form style="margin: 0rem; padding-bottom: 0">
        <template #es-form>
          <es-loader v-if="loading"/>
          <es-exception
            v-else-if="loadingError"
            style="margin: 0"
            exceptionMessage="There was an issue.
            Please try again or contact your administrator."
          />
          <table v-else style="width: 100%">
            <tbody>
              <tr>
                <th class="rule first-column">
                  Program Name
                </th>
                <th class="rule">
                  Last Index Date
                </th>
                <th class="rule">
                  Status
                </th>
                <th class="rule">
                  Match Enabled
                </th>
                <th class="rule">
                  CEP Enabled
                </th>
              </tr>
              <tr v-for="(program, index) in newValues" :key="index">
                <td class="first-column">
                  {{ program.description }}
                </td>
                <td>
                  <span v-if="!program.programIndex" title="Data is not available.">
                    N/A
                  </span>
                  <span
                    v-else-if="!program.programIndex.mostRecentFileDateLoaded"
                    title="Data does not exist.">
                    --
                  </span>
                  <span v-else>
                    {{ program.programIndex.mostRecentFileDateLoaded | formatDateMMDDYYYY }}
                  </span>
                </td>
                <td>
                  <span v-if="editCurrentPanel" class="buttons has-addons text-nowrap">
                    <button v-for="(option, index) in statusOptions" :key="index"
                      class="button button--form"
                      :class="{ 'is-active': program.active === option.code }"
                      @click="updateProgram('active', program.code, option.code)"
                    >
                      {{ option.description }}
                    </button>
                  </span>
                  <span v-else>
                    {{ program.active === true ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>
                  <span v-if="editCurrentPanel" class="buttons has-addons text-nowrap">
                    <button v-for="(option, index) in yesNoOptions" :key="index"
                      class="button button--form"
                      :class="{ 'is-active': program.matchEnabled === option.code }"
                      @click="updateProgram('matchEnabled', program.code, option.code)"
                    >
                      {{ option.description }}
                    </button>
                  </span>
                  <span v-else>
                    {{ program.matchEnabled === true ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td>
                  <span v-if="editCurrentPanel" class="buttons has-addons text-nowrap">
                    <button v-for="(option, index) in yesNoOptions" :key="index"
                      class="button button--form"
                      :class="{ 'is-active': program.cepEnabled === option.code }"
                      @click="updateProgram('cepEnabled', program.code, option.code)"
                    >
                      {{ option.description }}
                    </button>
                  </span>
                  <span v-else>
                    {{ program.cepEnabled === true ? 'Yes' : 'No' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </es-form>
    </div>
    <div
      v-if="editCurrentPanel"
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 5.15rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click.prevent="cancelPanel()"
        style="margin-right: 1rem"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        @click.prevent="updatePanel()"
      >
        Update
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faEdit,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
  },
  props: {
    editOtherPanels: {
      type: Boolean,
    },
  },
  data() {
    return this.initialData();
  },
  created() {
    this.statusOptions = [
      {
        code: true,
        description: 'Active',
      },
      {
        code: false,
        description: 'Inactive',
      },
    ];
    this.yesNoOptions = [
      {
        code: true,
        description: 'Yes',
      },
      {
        code: false,
        description: 'No',
      },
    ];
  },
  methods: {
    cancelPanel() {
      this.undoForm();
      this.hideNotification();
      this.editCurrentPanel = false;
      this.$emit('allowEditOtherPanels');
    },
    checkForFormChanges() {
      return this.compareObjects(this.newValues, this.cachedValues);
    },
    compareObjects(o, n) {
      return JSON.stringify(o) !== JSON.stringify(n);
    },
    editPanel() {
      this.editCurrentPanel = true;
      this.$emit('allowEditOtherPanels');
    },
    initialData() {
      return {
        editCurrentPanel: false,
        loading: false,
        loadingError: false,
        newValues: [],
        cachedValues: null,
      };
    },
    async loadDmPrograms() {
      return axios.get('programs?config=true');
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      const methodGetDmPrograms = this.loadDmPrograms();
      try {
        const promseGetPrograms = await methodGetDmPrograms;
        this.newValues = promseGetPrograms.data;
        this.updateCache();
      } catch (error) {
        this.loadingError = true;
        const apiMsg = `${error.response.data.message}`;
        this.showNotification('danger', [apiMsg]);
      } finally {
        this.loading = false;
      }
    },
    undoForm() {
      this.newValues = JSON.parse(JSON.stringify(this.cachedValues));
    },
    updateCache() {
      this.cachedValues = JSON.parse(JSON.stringify(this.newValues));
    },
    async updatePanel() {
      this.$emit('allowEditOtherPanels');
      const changes = this.checkForFormChanges();
      if (!changes) {
        this.editCurrentPanel = false;
        this.hideNotification();
        return;
      }
      try {
        await axios({
          method: 'post',
          url: '/programs/bulk-update-configs',
          data: this.newValues,
        });
        this.updateCache();
        this.showNotification('success', ['Programs have been updated successfully.']);
        this.editCurrentPanel = false;
      } catch (e) {
        this.$emit('allowEditOtherPanels');
        this.showNotification('danger', ['There was an issue. Please try again or contact your administrator.']);
      }
    },
    updateProgram(prop, code, option) {
      const item = this.newValues.filter(m => m.code === code);
      if (item && item.length === 1) {
        const type = item[0];
        type[prop] = option;
      }
    },
  },
  mounted() {
    this.onLoad();
  },
  filters: {
    formatDateMMDDYYYY(value) {
      if (value) {
        return moment(value).format('MM/DD/YYYY');
      }
      return 'N/A';
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    vertical-align: middle
    padding: 8px 0
  table td
    vertical-align: middle
    padding: 8px 0
  a.disabled
    color: #999
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  .property
    padding-left: 2rem
    font-size: 12px
    width: 50%
  .no-border
    border: 0
  .rule
    font-size: 12px
    color: #666
  .content
    padding: 7px 0
  .first-column
    padding-left: 0
  // .darkened
  //   background-color: #ccc !important
  //   font-weight: bold !important
</style>
